import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiSend, FiInbox, FiCheckCircle, FiArrowRight } from "react-icons/fi";
import { transfersApi, ownerUploadBase } from "../../services/transfersApi";
import { useTransferUpload } from "../../hooks/useTransferUpload";
import { MAIN_URL } from "../../utils/appHost";
import ExpirySelect from "../../components/share/ExpirySelect";
import TransferDropzone from "../../components/share/TransferDropzone";
import UploadProgressList from "../../components/share/UploadProgressList";
import OverallProgress from "../../components/share/OverallProgress";
import CopyLink from "../../components/share/CopyLink";
import { formatBytes } from "../../components/share/fileKinds";

/**
 * New transfer: "Send files" (upload + link right away + email when done)
 * or "Request files" (a link where a client uploads to you).
 */
export default function ShareHome() {
  const { t } = useTranslation();
  const [mode, setMode] = useState("send");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [expiry, setExpiry] = useState(1);
  const [limits, setLimits] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const [phase, setPhase] = useState("compose"); // compose | uploading | done
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const upload = useTransferUpload();

  useEffect(() => {
    transfersApi
      .stats()
      .then((res) => {
        setLimits(res.data);
        setExpiry(res.data.expiry_options[0] ?? 1);
      })
      .catch(() => setLimits({ expiry_options: [1], quota_bytes: 0, used_bytes: 0 }));
  }, []);

  const remaining = limits ? Math.max(0, limits.quota_bytes - limits.used_bytes) : 0;
  const overQuota = limits && upload.totalBytes > remaining;
  const overPerTransfer = limits?.max_per_transfer_bytes != null && upload.totalBytes > limits.max_per_transfer_bytes;

  const startOver = () => {
    setTransfer(null);
    setPhase("compose");
    setSubject("");
    setMessage("");
    setError("");
    upload.reset();
    transfersApi.stats().then((res) => setLimits(res.data)).catch(() => {});
  };

  const createTransfer = async () => {
    const { data } = await transfersApi.create({
      kind: mode,
      subject: subject.trim(),
      message: message.trim() || null,
      expiry_days: expiry,
    });
    return data.transfer;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    let created = null;
    try {
      // The link exists before the first byte is uploaded
      created = await createTransfer();
      setTransfer(created);
      setPhase("uploading");
      const { fileIds } = await upload.start({ base: ownerUploadBase(created.transfer_id) });
      if (fileIds.length === 0) {
        transfersApi.remove(created.transfer_id).catch(() => {}); // don't leave an empty link behind
        setTransfer(null);
        setError(t("share.upload.all_failed"));
        setPhase("compose");
        return;
      }
      const { data } = await transfersApi.finalize(created.transfer_id);
      setTransfer(data.transfer);
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || t("share.errors.generic"));
      setPhase(created ? "uploading" : "compose");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      setTransfer(await createTransfer());
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || t("share.errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Request link created ──────────────────────────────────────────────────
  if (transfer && transfer.kind === "request") {
    return (
      <div className="shr-card">
        <div className="shr-callout"><FiCheckCircle size={18} /><span>{t("share.request.created")}</span></div>
        <h1 className="shr-title">{transfer.subject}</h1>
        <p className="shr-subtitle">{t("share.request.created_desc")}</p>
        <CopyLink url={transfer.link} shareTitle={transfer.subject} />
        <div className="d-flex gap-2 mt-4 flex-wrap">
          <button type="button" className="shr-btn ghost" onClick={startOver}>{t("share.home.new_transfer")}</button>
          <Link to="/transfers" className="shr-btn ghost">{t("share.home.see_transfers")} <FiArrowRight /></Link>
        </div>
      </div>
    );
  }

  // ── Send: uploading / done ────────────────────────────────────────────────
  if (transfer && phase !== "compose") {
    const done = phase === "done";
    return (
      <div className="shr-card">
        {done ? (
          <div className="shr-callout"><FiCheckCircle size={18} /><span>{t("share.upload.done_email")}</span></div>
        ) : (
          <div className="shr-callout info"><FiSend size={18} /><span>{t("share.upload.link_ready")}</span></div>
        )}
        <h1 className="shr-title">{transfer.subject}</h1>
        <p className="shr-subtitle">
          {done
            ? t("share.upload.done_summary", { count: transfer.file_count, size: formatBytes(transfer.total_bytes) })
            : t("share.upload.in_progress")}
        </p>

        <CopyLink url={transfer.link} shareTitle={transfer.subject} />

        {error && <div className="shr-callout error mt-3">{error}</div>}

        {!done && (
          <OverallProgress
            uploadedBytes={upload.uploadedBytes}
            totalBytes={upload.totalBytes}
            speed={upload.speed}
            eta={upload.eta}
          />
        )}
        <UploadProgressList items={upload.items} />

        <div className="d-flex gap-2 mt-4 flex-wrap">
          {done ? (
            <>
              <button type="button" className="shr-btn primary" onClick={startOver}>{t("share.home.new_transfer")}</button>
              <Link to="/transfers" className="shr-btn ghost">{t("share.home.see_transfers")} <FiArrowRight /></Link>
            </>
          ) : (
            <button type="button" className="shr-btn ghost" onClick={upload.cancel} disabled={!upload.uploading}>
              {t("share.upload.cancel")}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Compose ───────────────────────────────────────────────────────────────
  const canSend = subject.trim() && upload.files.length > 0 && !overQuota && !overPerTransfer && !submitting;

  return (
    <div className="shr-card">
      <div className="shr-segmented" role="tablist">
        <button type="button" role="tab" aria-selected={mode === "send"} className={mode === "send" ? "active" : ""} onClick={() => setMode("send")}>
          <FiSend size={15} /> {t("share.home.tab_send")}
        </button>
        <button type="button" role="tab" aria-selected={mode === "request"} className={mode === "request" ? "active" : ""} onClick={() => setMode("request")}>
          <FiInbox size={15} /> {t("share.home.tab_request")}
        </button>
      </div>

      <h1 className="shr-title">{mode === "send" ? t("share.home.send_title") : t("share.home.request_title")}</h1>
      <p className="shr-subtitle">{mode === "send" ? t("share.home.send_subtitle") : t("share.home.request_subtitle")}</p>

      {error && <div className="shr-callout error">{error}</div>}

      <form onSubmit={mode === "send" ? handleSend : handleRequest}>
        <div className="shr-field">
          <label className="shr-label" htmlFor="shr-subject">{t("share.home.subject")}</label>
          <input
            id="shr-subject"
            className="shr-input"
            value={subject}
            maxLength={255}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={mode === "send" ? t("share.home.subject_placeholder_send") : t("share.home.subject_placeholder_request")}
            required
          />
        </div>

        <div className="shr-field">
          <label className="shr-label" htmlFor="shr-message">{t("share.home.message")}</label>
          <textarea
            id="shr-message"
            className="shr-textarea"
            value={message}
            maxLength={5000}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("share.home.message_placeholder")}
          />
        </div>

        <div className="shr-field">
          <span className="shr-label">{t("share.home.expiry")}</span>
          <ExpirySelect value={expiry} onChange={setExpiry} allowed={limits?.expiry_options || [1]} />
        </div>

        {mode === "send" && (
          <div className="shr-field">
            <TransferDropzone onFiles={upload.addFiles} />
            <UploadProgressList items={upload.items} onRemove={upload.removeFile} />
            {upload.files.length > 0 && (
              <p className="shr-muted" style={{ marginTop: 10 }}>
                {t("share.home.selected", { count: upload.files.length, size: formatBytes(upload.totalBytes) })}
              </p>
            )}
            {(overQuota || overPerTransfer) && (
              <div className="shr-callout error mt-2">
                <span>
                  {overPerTransfer
                    ? t("share.errors.over_per_transfer", { size: formatBytes(limits.max_per_transfer_bytes) })
                    : t("share.errors.over_quota", { size: formatBytes(remaining) })}{" "}
                  <a href={`${MAIN_URL}/plans`} style={{ color: "inherit", fontWeight: 600 }}>{t("share.errors.upgrade")}</a>
                </span>
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          className="shr-btn primary block"
          disabled={mode === "send" ? !canSend : !subject.trim() || submitting}
        >
          {mode === "send" ? <FiSend /> : <FiInbox />}
          {mode === "send" ? t("share.home.send_cta") : t("share.home.request_cta")}
        </button>
      </form>
    </div>
  );
}
